import React, { useEffect, useState } from 'react'

import Select from 'react-select'

import { Checkbox } from '@/components/CheckboxV2'
import { useListApplicationPartiesQuery } from '@/services/v2-application/applicationParties'
import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'

export function RecipientDocumentsSelect({
  dispatch,
  application,
  docTemplates,
  isLoadingDocTemplates,
}) {
  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: applicationParties } = useListApplicationPartiesQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const [selectedRecipients, setSelectedRecipients] = useState([])
  const [selectedDocsPerRecipient, setSelectedDocsPerRecipient] = useState({})

  const applicants =
    applicationProfiles?.results?.map((profile) => ({
      id: profile.id,
      type: profile.position === 1 ? 'Applicant' : 'Co-Applicant',
      profile: profile.profile,
      isSelected: false,
    })) || []

  const nonApplicants = []

  const { data: nonApplicantsData } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const coOwner = nonApplicantsData?.results?.find(
    (profile) => profile.profile_type.toString() === 'co_owner'
  )
  if (coOwner) {
    nonApplicants.push({
      id: coOwner.profile.id,
      type: 'Co-Owner',
      profile: coOwner.profile,
      isSelected: false,
    })
  }

  const noo = nonApplicantsData?.results?.find(
    (profile) => profile.profile_type.toString() === 'non_owning_occupant'
  )
  if (noo) {
    nonApplicants.push({
      id: noo.profile.id,
      type: 'Non-Owning Occupant',
      profile: noo.profile,
      isSelected: false,
    })
  }

  const allRecipients = [...applicants, ...nonApplicants]

  const handleRecipientChange = (recipientId, isChecked) => {
    let updatedRecipients

    if (isChecked) {
      const recipientToAdd = allRecipients.find((r) => r.id === recipientId)
      updatedRecipients = [...selectedRecipients, recipientToAdd]
    } else {
      updatedRecipients = selectedRecipients.filter((r) => r.id !== recipientId)

      const updatedDocs = { ...selectedDocsPerRecipient }
      delete updatedDocs[recipientId]
      setSelectedDocsPerRecipient(updatedDocs)
    }

    setSelectedRecipients(updatedRecipients)

    dispatch({
      type: 'setRecipients',
      payload: { recipients: updatedRecipients },
    })

    updateAllDocuments(selectedDocsPerRecipient)
  }

  const handleDocumentChange = (recipientId, selectedOptions) => {
    const updatedDocs = {
      ...selectedDocsPerRecipient,
      [recipientId]: selectedOptions,
    }
    setSelectedDocsPerRecipient(updatedDocs)

    dispatch({
      type: 'setRecipientDocuments',
      payload: {
        recipientId,
        documents: selectedOptions,
      },
    })

    updateAllDocuments(updatedDocs)
  }

  const updateAllDocuments = (docsPerRecipient) => {
    dispatch({
      type: 'setDocuments',
      payload: docsPerRecipient,
    })
  }

  useEffect(() => {
    if (allRecipients.length > 0) {
      const initiallySelected = allRecipients

      setSelectedRecipients(initiallySelected)

      dispatch({
        type: 'setRecipients',
        payload: { recipients: initiallySelected },
      })

      updateAllDocuments(selectedDocsPerRecipient)
    }
  }, [applicationProfiles, applicationParties])

  const firstLetter = (string) => string?.charAt(0) || ''

  return (
    <>
      <div>
        {allRecipients?.map((recipient, idx) => (
          <div
            className={idx === allRecipients.length - 1 ? '' : 'mb-6'}
            key={idx}
          >
            <div className='mb-2'>
              <label>
                {recipient.type} - {recipient.profile.full_name.split(' ')[0]}{' '}
                {firstLetter(recipient.profile.full_name.split(' ')[1])}
              </label>
              <div className='flex flex-row items-center'>
                <input
                  className='classic-input mr-2'
                  type='text'
                  value={recipient.profile.email || recipient.profile.full_name}
                  disabled
                />
                <Checkbox
                  checked={selectedRecipients.some((r) => r.id === recipient.id)}
                  onChange={(e) => handleRecipientChange(recipient.id, e.target.checked)}
                />
              </div>
            </div>

            <div className='mb-4'>
              <Select
                isMulti
                placeholder='Select documents...'
                options={docTemplates?.map((doc) => ({
                  value: doc.id,
                  label: doc.name,
                  ...doc,
                }))}
                isDisabled={!selectedRecipients.some((r) => r.id === recipient.id)}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                onChange={(selected) => handleDocumentChange(recipient.id, selected)}
                value={selectedDocsPerRecipient[recipient.id] || []}
                isLoading={isLoadingDocTemplates}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
